import * as React from "react";
import { useEffect, useState, type ChangeEvent, type MouseEvent } from "react";
import { getMailRoles, getMailOrganizations } from "../../../../api";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Box } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import ListItemText from "@mui/material/ListItemText";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import LaunchIcon from "@mui/icons-material/Launch";
import SnackbarCustom, {
  type SnackbarData,
} from "../../../../components/SnackBarCustom";
import type {
  MailOrganizationPreview,
  MailPreviewUser,
  MailRolePreview,
  RoleOrOrganizationParams,
} from "../../../../types/mail";

type PreviewColumn = {
  id: "m_organization" | "users";
  label: string;
  minWidth: string;
  align?: "left" | "right";
  operation?: boolean;
  isSmallScreen: boolean;
};

type PreviewRow = MailRolePreview | MailOrganizationPreview;

const columns: PreviewColumn[] = [
  {
    id: "m_organization",
    label: "送信先",
    minWidth: "120px",
    isSmallScreen: true,
  },
  {
    id: "users",
    label: "送信予定件数",
    minWidth: "120px",
    operation: true,
    isSmallScreen: true,
  },
];

const OperationButton = ({ value }: { value?: MailPreviewUser[] }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleMenu = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  if (!value) return null;

  return (
    <>
      <IconButton
        color="primary"
        size="small"
        sx={{ pointerEvents: "auto" }}
        onClick={handleMenu}
      >
        <LaunchIcon sx={{ width: "20px" }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Box sx={{ paddingX: "8px",maxHeight: "500px",overflowY: "auto" }}>
        <Box sx={{ display: "flex", justifyContent: "end", paddingX: "8px",position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1 }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Table size="small">
          <TableHead sx={{position: "sticky", top: 40, backgroundColor: "#fff", zIndex: 1 }}>
            <TableRow >
              <TableCell>名前</TableCell>
              <TableCell>メールアドレス</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {value.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.open_id}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ height: "16px",position: "sticky", bottom: 0}} />
        </Box>
      </Menu>
    </>
  );
};

type PreviewTableProps = {
  roleOrOrganization: RoleOrOrganizationParams;
};

const PreviewTable = ({ roleOrOrganization }: PreviewTableProps) => {
  const [selectedRow, setSelectedRow] = useState<PreviewRow | null>(null);
  //*ポップアップ設定 */
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  // const [popoverData, setPopoverData] = useState(null);

  const handleLocationCodeClick = (row: PreviewRow) => {
    setSelectedRow(row);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  const [previews, setPreviews] = useState<PreviewRow[] | null>(null);
  const [totalCount, setTotalCount] = useState<number | undefined>();

  const fetchPreviewData = async () => {
    try {
      let response;
      if (
        roleOrOrganization.m_role_id &&
        roleOrOrganization.m_role_id.length > 0
      ) {
        response = await getMailRoles({
          m_role_id: roleOrOrganization.m_role_id,
        });
        if (response.status !== "ok") {
          return;
        }
        const data = response.m_roles;
        const totalCount = response.total_count;
        const status = response;
        console.log("プレビュー取得", response);
        setPreviews(data);
        setTotalCount(totalCount);
        //スナックバーステート
        setSnackBarstatus(status);
        setSnackbarOpen(true);
      } else if (
        roleOrOrganization.m_organization_id &&
        roleOrOrganization.m_organization_id.length > 0
      ) {
        response = await getMailOrganizations({
          m_organization_id: roleOrOrganization.m_organization_id,
        });
        if (response.status !== "ok") {
          return;
        }
        const data = response.m_organizations;
        const totalCount = response.total_count;
        const status = response;
        console.log("プレビュー取得", response);
        setPreviews(data);
        setTotalCount(totalCount);
        //スナックバーステート
        setSnackBarstatus(status);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  useEffect(() => {
    if (
      roleOrOrganization.m_role_id !== null ||
      roleOrOrganization.m_organization_id !== null
    ) {
      fetchPreviewData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleOrOrganization.m_role_id, roleOrOrganization.m_organization_id]);

  //検索結果ページネーション
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  return (
    <>
      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                    sx={{
                      color: "outline.dark",
                      fontWeight: "bold",
                      borderBottom: 2,
                      padding: "8px",
                      "@media screen and (max-width:900px)": {
                        display:
                          column.isSmallScreen === true ? "table-cell" : "none",
                      },
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* {rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) */}
              {/* {row{}e(page * rowsPerPage, page * rowsPerPage + rowsPerPage) */}
              {previews?.map((row, rowIndex) => {
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={`row_${rowIndex}_${row.id || "no-id"}`}
                    onClick={() => handleLocationCodeClick(row)}
                    sx={{
                      pointerEvents: "none",
                      "@media screen and (max-width:900px)": {
                        cursor: "pointer",
                        pointerEvents: "auto",
                      },
                    }}
                  >
                    {columns.map((column, columnIndex) => {
                      const usersValue =
                        column.id === "users" ? row.users : undefined;
                      return (
                        <TableCell
                          key={`row_${rowIndex}_col_${columnIndex}_${column.id}`}
                          align={column.align}
                          sx={{
                            padding: "8px",
                            "@media screen and (max-width:900px)": {
                              display:
                                column.isSmallScreen === true
                                  ? "table-cell"
                                  : "none",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {column.id === "users" ? (
                              <>{row.users?.length || 0}件</>
                            ) : column.id === "m_organization" ? (
                              <>{row.name || ""}</>
                            ) : null}

                            {column.operation ? (
                              <OperationButton value={usersValue} />
                            ) : null}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Dialog open={isPopoverOpen} onClose={handlePopoverClose}>
          <Box
            sx={{
              display: "flex",
              height: "56px",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px",
              borderBottom: "2px solid #CCC",
              position: "sticky",
              top: 0,
              backgroundColor: "#fff",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                paddingLeft: "8px",
                fontWeight: "bold",
                fontSize: "20px",
              }}
            >
              被害履歴詳細
            </Box>
            <IconButton
              aria-label="close"
              onClick={handlePopoverClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
            <TableBody>
              {selectedRow &&
                columns.slice(0, 4).map((column, columnIndex) => {
                  const usersValue =
                    column.id === "users" ? selectedRow.users : undefined;
                  return (
                    <TableRow
                      role="checkbox"
                      tabIndex={-1}
                      key={`dialog_row_${columnIndex}_${column.id}`}
                    >
                      <TableCell
                        align={column.align}
                        sx={{
                          color: "outline.dark",
                          fontWeight: "bold",
                          boxSizing: "border-box",
                          width: "calc((100vw - 64px) / 2)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            WebkitWritingMode: "horizontal-tb",
                            height: "100%",
                          }}
                        >
                          {column.label}
                        </Box>
                      </TableCell>
                      <TableCell
                        align={column.align}
                        sx={{
                          writingMode: "horizontal-tb",
                          boxSizing: "border-box",
                          width: "calc((100vw - 64px) / 2)",
                          overflowWrap: "break-word",
                        }}
                      >
                        {column.id === "users" ? (
                          <>{selectedRow.users?.length || 0}件</>
                        ) : column.id === "m_organization" ? (
                          <>
                            {(
                              (selectedRow as MailOrganizationPreview)
                                .m_organization as
                                | { department_name?: string }
                                | undefined
                            )?.department_name || ""}
                          </>
                        ) : null}
                        {column.operation ? (
                          <OperationButton value={usersValue} />
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </Dialog>

        <TablePagination
          rowsPerPageOptions={[10, 30, 50, 100]}
          component="div"
          count={totalCount ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions":
            {
              margin: "0 !important",
              padding: "0 !important",
            },
          }}
          labelRowsPerPage="表示件数:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`}
        />
        <SnackbarCustom
          data={snackBarstatus}
          openStatus={snackbarOpen}
          onClose={() => setSnackbarOpen(false)}
        />
      </Paper>
    </>
  );
};
export default PreviewTable;
